import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Loader2, Clock, Sparkles, AlertTriangle, ArrowLeft, ShieldCheck, BrainCircuit, ActivitySquare, BadgeCheck } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const fmt = (v) => new Intl.NumberFormat("fr-FR").format(v);

const MODULE_ICONS = {
  assurances: ShieldCheck,
  perimes: BrainCircuit,
  dispensation: ActivitySquare,
};

const PACK_TONE = {
  essentiel: { ring: "#025946", soft: "#E6F2EE", chip: "#025946" },
  duo: { ring: "#0F766E", soft: "#E0F2F1", chip: "#0F766E" },
  institution: { ring: "#C9A25E", soft: "#FAF3E2", chip: "#C9A25E" },
};

export default function SubscriptionSelectPage() {
  const [packs, setPacks] = useState([]);
  const [modules, setModules] = useState([]);
  const [trialDays, setTrialDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [myPlan, setMyPlan] = useState(null);
  const [selectedPack, setSelectedPack] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const navigate = useNavigate();
  const { user, refreshUser, logout } = useAuth();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/subscription-plans/packs`),
      axios.get(`${API}/subscription-plans/my-plan`, { headers: getAuthHeaders() }).catch(() => null),
    ]).then(([res, myRes]) => {
      setPacks(res.data.packs || []);
      setModules(res.data.modules || []);
      setTrialDays(res.data.trial_days || 30);
      if (myRes) {
        setMyPlan(myRes.data);
        if (myRes.data?.pack) {
          setSelectedPack(myRes.data.pack);
          setSelectedModules(myRes.data.modules || []);
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const currentPack = useMemo(() => packs.find(p => p.id === selectedPack), [packs, selectedPack]);

  const onPickPack = (packId) => {
    setSelectedPack(packId);
    if (packId === "institution") {
      setSelectedModules(["assurances", "perimes", "dispensation"]);
    } else if (myPlan?.pack === packId) {
      setSelectedModules(myPlan.modules || []);
    } else {
      setSelectedModules([]);
    }
  };

  const toggleModule = (modId) => {
    if (!currentPack) return;
    if (currentPack.id === "institution") return;
    setSelectedModules((prev) => {
      const exists = prev.includes(modId);
      if (exists) return prev.filter(m => m !== modId);
      if (prev.length >= currentPack.modules_count) {
        toast.info(`Le ${currentPack.name} permet ${currentPack.modules_count} module${currentPack.modules_count > 1 ? "s" : ""} maximum.`);
        return prev;
      }
      return [...prev, modId];
    });
  };

  const canConfirm = currentPack && (
    currentPack.id === "institution" ||
    selectedModules.length === currentPack.modules_count
  );

  const isCurrentSelection = useMemo(() => {
    if (!myPlan?.pack || !selectedPack) return false;
    if (myPlan.pack !== selectedPack) return false;
    if (selectedPack === "institution") return true;
    const a = [...(myPlan.modules || [])].sort().join(",");
    const b = [...selectedModules].sort().join(",");
    return a === b;
  }, [myPlan, selectedPack, selectedModules]);

  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/subscription-plans/select`,
        { pack: selectedPack, modules: selectedModules },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message);
      await refreshUser();
      if (res.data.status === "trial" || res.data.status === "active") {
        navigate("/hub");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sélection");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-700" />
      </div>
    );
  }

  // Pending payment screen
  const isPendingPayment = user?.subscription_status === "pending_payment";
  if (isPendingPayment) {
    return (
      <div className="min-h-screen py-16 px-4 flex items-center justify-center bg-[#F9F8F6]" data-testid="subscription-pending-page">
        <div className="max-w-lg w-full bg-white rounded-2xl border-2 border-amber-200 shadow-xl p-10 text-center">
          <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-3">En attente de validation</h2>
          <p className="text-slate-600 mb-6">
            Votre plan <strong className="text-emerald-700">{myPlan?.plan?.name || user.subscription_plan}</strong> est en cours de validation par l'administrateur CMES.
          </p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => refreshUser().then(u => { if (u?.subscription_status === "active" || u?.subscription_status === "trial") navigate("/hub"); else toast.info("Votre abonnement est toujours en attente."); })}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6" data-testid="check-status-btn">
              Vérifier le statut
            </Button>
            <Button onClick={logout} variant="outline" className="rounded-full px-6 text-slate-600 border-slate-300" data-testid="logout-btn">
              Se déconnecter
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const isTrial = myPlan?.is_trial;
  const trialDaysRemaining = myPlan?.trial_days_remaining;
  const trialExpired = myPlan?.trial_expired;
  const hasPlan = !!myPlan?.pack || !!user?.subscription_plan;
  const isActive = user?.subscription_status === "active" && !isTrial;
  const canGoBack = hasPlan && (isTrial || isActive);

  return (
    <div className="min-h-screen py-10 px-4 bg-[#F9F8F6]" data-testid="subscription-select-page">
      <div className="max-w-6xl mx-auto">

        {canGoBack && (
          <Button onClick={() => navigate("/hub")} variant="ghost" className="mb-4 text-slate-500 gap-1 hover:text-slate-700" data-testid="back-to-hub">
            <ArrowLeft className="w-4 h-4" /> Retour au Hub
          </Button>
        )}

        {isTrial && !trialExpired && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="trial-banner">
            <Sparkles className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-emerald-900">
                Essai gratuit en cours — <strong>{trialDaysRemaining} jour{trialDaysRemaining > 1 ? "s" : ""} restant{trialDaysRemaining > 1 ? "s" : ""}</strong>
              </p>
              <p className="text-xs text-emerald-700">Vous pouvez changer de pack et de modules à tout moment pendant votre essai.</p>
            </div>
          </div>
        )}

        {trialExpired && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="trial-expired-banner">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-800">Votre essai gratuit est terminé</p>
              <p className="text-xs text-red-600">Contactez l'administrateur CMES pour activer votre abonnement.</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-emerald-700 mb-4">─── Choisissez votre formule</p>
          <h1 className="text-3xl sm:text-5xl font-serif text-slate-900 tracking-tight mb-3">
            {hasPlan ? "Modifiez votre plan" : "Votre compte est validé."}
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto text-base">
            {hasPlan
              ? "Changez de pack ou de modules à tout moment. Le changement est immédiat."
              : `Sélectionnez votre pack et les modules adaptés à votre officine. Essai gratuit de ${trialDays} jours, sans engagement.`}
          </p>
        </div>

        {/* STEP 1: Pack selection */}
        <div className="mb-8">
          <h2 className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-slate-700 mb-4">─── Étape 1 · Choisissez votre pack</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {packs.map((pack) => {
              const tone = PACK_TONE[pack.id] || PACK_TONE.essentiel;
              const isSel = selectedPack === pack.id;
              return (
                <button
                  key={pack.id}
                  onClick={() => onPickPack(pack.id)}
                  data-testid={`pack-${pack.id}`}
                  className={`text-left bg-white rounded-2xl border-2 p-7 transition-all hover:-translate-y-1 hover:shadow-lg relative ${isSel ? "shadow-xl" : "border-slate-200"}`}
                  style={isSel ? { borderColor: tone.ring, boxShadow: `0 10px 40px -10px ${tone.ring}40` } : {}}
                >
                  {pack.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="text-[10px] font-bold px-3 py-1 rounded-full text-white" style={{ background: tone.chip }}>★ {pack.badge}</span>
                    </div>
                  )}
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] mb-2" style={{ color: tone.ring }}>PACK</p>
                  <h3 className="text-3xl font-serif text-slate-900 mb-1">{pack.name.replace("Pack ", "")}</h3>
                  <p className="text-xs text-slate-500 mb-5">{pack.subtitle}</p>
                  <div className="mb-4">
                    <span className="text-4xl font-serif font-light text-slate-900">{fmt(pack.price)}</span>
                    <span className="text-sm text-slate-500 ml-2">FCFA / {pack.period}</span>
                    {pack.saving && (
                      <p className="mt-1 inline-block text-[10px] font-bold px-2 py-0.5 rounded text-emerald-800 bg-emerald-100">Économie {fmt(pack.saving)} F/mois</p>
                    )}
                  </div>
                  <p className="text-[13px] text-slate-600 leading-relaxed">{pack.description}</p>
                  {isSel && (
                    <div className="mt-5 pt-4 border-t flex items-center gap-2 text-xs font-semibold" style={{ borderColor: tone.soft, color: tone.ring }}>
                      <Check className="w-4 h-4" /> Pack sélectionné
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* STEP 2: Module selection */}
        {currentPack && (
          <div className="mb-8" data-testid="step-modules">
            <h2 className="text-[11px] font-extrabold uppercase tracking-[0.3em] text-slate-700 mb-4">
              ─── Étape 2 · {currentPack.id === "institution" ? "Vos 3 modules inclus" : `Choisissez ${currentPack.modules_count} module${currentPack.modules_count > 1 ? "s" : ""}`}
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {modules.map((mod) => {
                const Icon = MODULE_ICONS[mod.id] || ShieldCheck;
                const isSel = selectedModules.includes(mod.id);
                const disabled = currentPack.id === "institution" || (!isSel && selectedModules.length >= currentPack.modules_count);
                return (
                  <button
                    key={mod.id}
                    onClick={() => toggleModule(mod.id)}
                    disabled={currentPack.id === "institution"}
                    data-testid={`module-${mod.id}`}
                    className={`text-left bg-white rounded-2xl border-2 p-6 transition-all relative ${isSel ? "border-emerald-700 shadow-lg" : "border-slate-200"} ${disabled && !isSel ? "opacity-50 cursor-not-allowed" : "hover:-translate-y-1 hover:shadow-md cursor-pointer"}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-emerald-700" />
                      </div>
                      {isSel && (
                        <div className="w-6 h-6 rounded-full bg-emerald-700 flex items-center justify-center">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] font-extrabold uppercase tracking-[0.25em] text-emerald-700 mb-1">{mod.tag}</p>
                    <h3 className="text-lg font-bold text-slate-900 mb-3">{mod.name}</h3>
                    <ul className="space-y-1.5">
                      {mod.features.slice(0, 4).map((f) => (
                        <li key={f} className="flex items-start gap-1.5 text-xs text-slate-600">
                          <Check className="w-3 h-3 text-emerald-600 shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                      {mod.features.length > 4 && (
                        <li className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">+ {mod.features.length - 4} autres fonctionnalités</li>
                      )}
                    </ul>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 3: Confirm */}
        {currentPack && (
          <div className="bg-gradient-to-br from-slate-900 to-emerald-950 rounded-2xl p-8 lg:p-10 text-white" data-testid="confirm-card">
            <div className="grid lg:grid-cols-3 gap-6 items-center">
              <div className="lg:col-span-2">
                <p className="text-[10px] font-extrabold uppercase tracking-[0.3em] text-amber-300 mb-3">─── Récapitulatif</p>
                <h3 className="text-2xl font-serif font-light mb-2">{currentPack.name}</h3>
                <p className="text-white/70 text-sm mb-4">
                  {currentPack.id === "institution"
                    ? "Les 3 modules CMES inclus : Assurances · Périmés · Dispensation"
                    : `${selectedModules.length}/${currentPack.modules_count} module${currentPack.modules_count > 1 ? "s" : ""} sélectionné${selectedModules.length > 1 ? "s" : ""}` + (selectedModules.length > 0 ? ` : ${selectedModules.map(m => modules.find(x => x.id === m)?.name).join(" + ")}` : "")}
                </p>
                <div className="flex flex-wrap gap-3 text-xs text-white/70">
                  <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-amber-300" /> Essai {trialDays} jours gratuit</span>
                  <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-amber-300" /> Support WhatsApp 7j/7</span>
                  <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-amber-300" /> Mobile Money</span>
                  <span className="flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-amber-300" /> Sans engagement</span>
                </div>
              </div>
              <div className="text-right">
                <p className="text-5xl font-serif font-light mb-1">{fmt(currentPack.price)}</p>
                <p className="text-xs text-white/60 mb-4">FCFA / {currentPack.period} · HT</p>
                <Button
                  onClick={confirm}
                  disabled={!canConfirm || submitting || isCurrentSelection}
                  data-testid="confirm-pack-btn"
                  className="w-full bg-amber-400 hover:bg-amber-300 text-slate-900 font-bold rounded-full py-6 text-sm shadow-xl"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> :
                    isCurrentSelection ? "Formule actuelle" :
                    canConfirm ? <>Activer mon plan <ArrowRight className="w-4 h-4 ml-2" /></> : "Sélectionnez vos modules"}
                </Button>
              </div>
            </div>
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-8">
          Tous les packs incluent un essai gratuit de {trialDays} jours. Aucun paiement immédiat. Satisfait ou remboursé.
        </p>
      </div>
    </div>
  );
}
